import { STAFFER_PET_SOURCE_SIZE_PX as SOURCE_SIZE_PX } from "@/lib/constants";

import type { StafferPetPalette, StafferPetPaletteId } from "@/types";

const SPRITE_CACHE_LIMIT = 64;
const RAINBOW_STOPS: Rgb[] = [
  [255, 64, 105],
  [255, 150, 40],
  [255, 222, 50],
  [40, 220, 140],
  [35, 170, 255],
  [140, 90, 255],
  [245, 90, 215],
];
// Rainbow pixels are shaded by source brightness: base + brightness / range.
// A higher base keeps mid-tones vivid instead of darkening them.
const RAINBOW_SHADE_BASE = 0.7;
const RAINBOW_SHADE_RANGE = 400;

const hexToRgb = (hex: string): [number, number, number] => [
  Number.parseInt(hex.slice(1, 3), 16),
  Number.parseInt(hex.slice(3, 5), 16),
  Number.parseInt(hex.slice(5, 7), 16),
];

type Rgb = [number, number, number];

// Parsed once per sprite; the pixel loop below runs ~65k times.
const getBandColors = (palette: StafferPetPalette): Rgb[] =>
  [
    palette.outline,
    palette.shadow,
    palette.body,
    palette.highlight,
    palette.belly,
    palette.eyeHighlight,
  ].map(hexToRgb);

const getPixelColor = (brightness: number, bandColors: Rgb[]): Rgb => {
  if (brightness < 52) return bandColors[0];
  if (brightness < 88) return bandColors[1];
  if (brightness < 142) return bandColors[2];
  if (brightness < 194) return bandColors[3];
  if (brightness < 232) return bandColors[4];
  return bandColors[5];
};

const spriteCache = new Map<string, { url: Promise<string>; users: number }>();

const createSpriteUrl = async (
  spriteSrc: string,
  bandColors: Rgb[],
  paletteId?: StafferPetPaletteId,
): Promise<string> => {
  const image = new Image();
  image.src = spriteSrc;
  await image.decode();

  const canvas = document.createElement("canvas");
  canvas.width = SOURCE_SIZE_PX;
  canvas.height = SOURCE_SIZE_PX;
  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (!context) throw new Error("Could not prepare the pet sprite");

  context.imageSmoothingEnabled = false;
  context.drawImage(image, 0, 0, SOURCE_SIZE_PX, SOURCE_SIZE_PX);

  const imageData = context.getImageData(0, 0, SOURCE_SIZE_PX, SOURCE_SIZE_PX);
  for (let index = 0; index < imageData.data.length; index += 4) {
    if (imageData.data[index + 3] === 0) continue;

    const sourceRed = imageData.data[index];
    const sourceGreen = imageData.data[index + 1];
    const sourceBlue = imageData.data[index + 2];
    const brightness =
      sourceRed * 0.299 + sourceGreen * 0.587 + sourceBlue * 0.114;
    let [red, green, blue] = getPixelColor(brightness, bandColors);
    if (paletteId === "RAINBOW" && brightness >= 88 && brightness < 232) {
      const x = (index / 4) % SOURCE_SIZE_PX;
      const y = Math.floor(index / 4 / SOURCE_SIZE_PX);
      const position = Math.min(
        5.999,
        ((x * 0.35 + y * 0.65) / SOURCE_SIZE_PX) * 6,
      );
      const stop = Math.floor(position);
      const blend = position - stop;
      const shade = RAINBOW_SHADE_BASE + brightness / RAINBOW_SHADE_RANGE;
      [red, green, blue] = RAINBOW_STOPS[stop].map((channel, channelIndex) =>
        Math.min(
          255,
          Math.round(
            (channel * (1 - blend) +
              RAINBOW_STOPS[stop + 1][channelIndex] * blend) *
              shade,
          ),
        ),
      ) as Rgb;
    }
    imageData.data[index] = red;
    imageData.data[index + 1] = green;
    imageData.data[index + 2] = blue;
  }

  context.putImageData(imageData, 0, 0);

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((imageBlob) => {
      if (imageBlob) resolve(imageBlob);
      else reject(new Error("Could not encode the pet sprite"));
    }, "image/png");
  });
  return URL.createObjectURL(blob);
};

const trimCache = () => {
  for (const [key, entry] of spriteCache) {
    if (spriteCache.size <= SPRITE_CACHE_LIMIT) break;
    if (entry.users > 0) continue;

    spriteCache.delete(key);
    void entry.url.then(
      (url) => {
        URL.revokeObjectURL(url);
      },
      () => undefined,
    );
  }
};

export const stafferPetImages = {
  retain: (
    spriteSrc: string,
    palette: StafferPetPalette,
    paletteId?: StafferPetPaletteId,
  ) => {
    const bandColors = getBandColors(palette);
    const key = JSON.stringify([spriteSrc, paletteId, bandColors]);
    let entry = spriteCache.get(key);
    if (!entry) {
      const newEntry = {
        url: createSpriteUrl(spriteSrc, bandColors, paletteId),
        users: 0,
      };
      // Let future mounts retry a failed image without retaining it in the cache.
      void newEntry.url.catch(() => {
        if (spriteCache.get(key) === newEntry) spriteCache.delete(key);
      });
      entry = newEntry;
    }

    // Share pending encodes and keep URLs valid while a sprite uses them.
    entry.users++;
    spriteCache.delete(key);
    spriteCache.set(key, entry);
    trimCache();

    return {
      url: entry.url,
      release: () => {
        entry.users--;
        trimCache();
      },
    };
  },
};
